import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';
import { auth } from '../firebase/config';
import {
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from 'firebase/auth';
import {
  deleteAllUserData,
  submitDeletionRequest,
  type DeletionRequestType,
} from '../utils/deleteUserData';
import Logo from '../components/Logo';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const DeleteAccountPage: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const emailParam = searchParams.get('email') || '';
  const [requestType, setRequestType] = useState<DeletionRequestType>('full_account');
  const [email, setEmail] = useState(currentUser?.email || emailParam);

  useEffect(() => {
    if (emailParam && !currentUser?.email) setEmail(emailParam);
  }, [emailParam, currentUser?.email]);
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [confirmCheckbox, setConfirmCheckbox] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
  };

  const handleSubmitRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setMessage({ type: 'error', text: 'Please enter the email address associated with your A(rDx)HD account.' });
      return;
    }

    if (!RECAPTCHA_SITE_KEY) {
      if (!confirmCheckbox) {
        setMessage({ type: 'error', text: 'Please confirm that you want to request deletion.' });
        return;
      }
    } else {
      if (!recaptchaToken) {
        setMessage({ type: 'error', text: 'Please complete the reCAPTCHA verification.' });
        return;
      }
    }

    setLoading(true);
    try {
      await submitDeletionRequest(trimmedEmail, requestType, additionalDetails || undefined);
      setMessage({
        type: 'success',
        text: `We've received your deletion request. Your request will be processed within 30 days. You will receive an email confirmation at ${trimmedEmail} once your request has been processed.`,
      });
      setEmail('');
      setAdditionalDetails('');
      setConfirmCheckbox(false);
      setRecaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to submit request. Please try again or contact us.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccountNow = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.email) return;
    setMessage(null);

    const pwd = password.trim();
    if (!pwd) {
      setMessage({ type: 'error', text: 'Please enter your password to confirm account deletion.' });
      return;
    }

    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(currentUser.email, pwd);
      await reauthenticateWithCredential(auth.currentUser!, credential);
      await deleteAllUserData(currentUser);
      await deleteUser(auth.currentUser!);
      await logout();
      setMessage({
        type: 'success',
        text: 'Your account and all associated data have been permanently deleted. You will be redirected home.',
      });
      setTimeout(() => navigate('/', { replace: true }), 3000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Account deletion failed.';
      const friendly =
        msg.includes('wrong-password') || msg.includes('invalid-credential')
          ? 'Incorrect password. Please try again.'
          : msg;
      setMessage({ type: 'error', text: friendly });
    } finally {
      setLoading(false);
      setPassword('');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F8E8] to-[#F5FAF0] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <Link
            to="/"
            className="inline-flex items-center text-sleek-700 hover:text-sleek-800 focus-visible-ring rounded px-2 py-1"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Return to Home
          </Link>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="p-6 md:p-8 border-b border-gray-200">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
              Account &amp; Data Deletion Request
            </h1>
            <p className="text-gray-600 text-sm">
              This page is provided by <Logo className="text-base" /> (A(rDx)HD Platform) to allow you to request
              deletion of your account or specific data.
            </p>
          </div>

          <div className="p-6 md:p-8 space-y-8">
            {/* Logged-in: delete now */}
            {currentUser && (
              <section className="space-y-4">
                <h2 className="text-lg font-semibold text-gray-900">Delete your account now</h2>
                <p className="text-gray-600 text-sm">
                  You are signed in as <strong>{currentUser.email}</strong>. Enter your password below to
                  permanently delete your account and all associated data immediately.
                </p>
                <form onSubmit={handleDeleteAccountNow} className="space-y-4">
                  <div>
                    <label htmlFor="password-now" className="block text-sm font-medium text-gray-700 mb-1">
                      Password *
                    </label>
                    <input
                      id="password-now"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="input-field"
                      placeholder="Your account password"
                      autoComplete="current-password"
                      disabled={loading}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full font-semibold py-3 px-6 rounded-lg transition-all duration-300 text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70"
                  >
                    {loading ? 'Deleting…' : 'Permanently delete my account'}
                  </button>
                </form>
                <hr className="border-gray-200" />
              </section>
            )}

            {/* Request deletion (always show for non-logged-in; optional for logged-in) */}
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {currentUser ? 'Or submit a request (e.g. for a different email)' : 'Request account or data deletion'}
              </h2>
              <div className="text-gray-600 text-sm space-y-2">
                <p className="font-medium text-gray-700">How to request deletion:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Select deletion type: full account deletion or deletion of specific data.</li>
                  <li>Enter the email address associated with your A(rDx)HD account.</li>
                  {RECAPTCHA_SITE_KEY && (
                    <li>Complete the reCAPTCHA verification.</li>
                  )}
                  <li>Click &quot;Submit request&quot;. We will process your request within 30 days and email you when done.</li>
                </ul>
              </div>

              <form onSubmit={handleSubmitRequest} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Request type *</label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="requestType"
                        checked={requestType === 'full_account'}
                        onChange={() => setRequestType('full_account')}
                        className="mt-1 text-sleek-600 focus:ring-sleek-500"
                      />
                      <span className="text-gray-700">
                        <strong>Delete entire account</strong>: Permanently delete your account and all associated data.
                      </span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="requestType"
                        checked={requestType === 'specific_data'}
                        onChange={() => setRequestType('specific_data')}
                        className="mt-1 text-sleek-600 focus:ring-sleek-500"
                      />
                      <span className="text-gray-700">
                        <strong>Delete specific data only</strong>: Delete selected data types without deleting your account. We will follow up by email.
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email address *
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-field"
                    placeholder="Email associated with your A(rDx)HD account"
                    autoComplete="email"
                    disabled={!!currentUser}
                  />
                </div>

                <div>
                  <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-1">
                    Additional details (optional)
                  </label>
                  <textarea
                    id="details"
                    value={additionalDetails}
                    onChange={(e) => setAdditionalDetails(e.target.value)}
                    className="input-field min-h-[80px]"
                    placeholder="Any additional information about your deletion request…"
                    rows={3}
                    disabled={loading}
                  />
                </div>

                {RECAPTCHA_SITE_KEY ? (
                  <div>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={handleRecaptchaChange}
                      onExpired={() => setRecaptchaToken(null)}
                      theme="light"
                    />
                  </div>
                ) : (
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={confirmCheckbox}
                      onChange={(e) => setConfirmCheckbox(e.target.checked)}
                      className="mt-1 rounded border-gray-300 text-sleek-600 focus:ring-sleek-500"
                    />
                    <span className="text-gray-700">
                      I confirm that I want to request deletion of my account or data as selected above.
                    </span>
                  </label>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full"
                >
                  {loading ? 'Submitting…' : 'Submit request'}
                </button>
              </form>
            </section>

            {message && (
              <div
                className={`p-4 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
                role="alert"
              >
                {message.text}
              </div>
            )}

            {/* What data can be deleted */}
            <section className="text-sm text-gray-600 space-y-2 pt-4 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900">What data can be deleted</h3>
              <p className="font-medium text-gray-700">Data that can be deleted:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Your account and profile (name, email)</li>
                <li>Authentication credentials</li>
                <li>Consent and game progress</li>
                <li>All game data (Pattern Match, Bounce Back, Flutter Focus, Berry Blitz)</li>
                <li>Assessment and results data</li>
              </ul>
              <p className="font-medium text-gray-700 mt-2">Data that may be retained:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Aggregated, anonymized data that cannot be linked to you</li>
                <li>Data required for legal compliance or dispute resolution</li>
                <li>Backup copies (deleted within 90 days of processing your request)</li>
              </ul>
              <p className="mt-2">
                Your request will be processed within 30 days. After that, requested data will be permanently deleted from our systems. Backups may be retained for up to 90 days for disaster recovery, then deleted.
              </p>
            </section>
          </div>

          <div className="px-6 md:px-8 py-4 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
            Questions about account or data deletion?{' '}
            <Link to="/contact" className="text-sleek-600 hover:text-sleek-700 font-medium focus-visible-ring">
              Contact us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
